import axios from "axios";
import { baseURL } from "../globals/constants";
import { showErrorToast } from "../utils/Toast";
import { resizeImage } from "../utils/resizeImage";

const headers = () => ({ headers: { Accept: "application/json", Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
const url = `${baseURL}/v1/marketing`;
const fail = (error: any, fallback: string) => {
  showErrorToast(error?.response?.data?.message || fallback, false);
  return null;
};

export type MailBlockType = "heading" | "text" | "image" | "button" | "divider" | "spacer" | "imageText";

/** One element of the mail. The order of the blocks is the order they are sent in. */
export interface MailBlock {
  type: MailBlockType;
  text?: string;
  url?: string;
  imageUrl?: string;
  alt?: string;
  align?: "left" | "center" | "right";
  size?: string;
  /** The grid: row, first column (0-11) and columns spanned (1-12). Missing = a full-width row of its own. */
  row?: number;
  x?: number;
  w?: number;
}

export interface MailTemplate {
  id?: string;
  name: string;
  /** The mail built from blocks. Templates from before the editor use headline/body/button instead. */
  blocks?: MailBlock[];
  subject: string;
  preheader?: string;
  headline?: string;
  body: string;
  buttonText?: string;
  buttonUrl?: string;
  imageUrl?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export type MailTrigger =
  | "tenantCreated"
  | "noBreefYet"
  | "draftNotSent"
  | "noBreefForDays"
  | "subscriptionEnding"
  | "breefCompleted"
  | "paymentFailed"
  | "subscriptionCancelled"
  | "breefEndingSoon"
  | "paymentSucceeded"
  | "firstBreefCompleted"
  | "signupNotCompleted";

export interface MailFlow {
  id?: string;
  name: string;
  /** service = about the customer's own account (goes to everyone), marketing = needs a yes */
  kind: "service" | "marketing";
  trigger: MailTrigger;
  waitDays: number;
  templateId: string;
  active: boolean;
  /** How many customers it would write to right now */
  reach?: number;
  lastRunAt?: string;
  lastSentCount?: number;
}

export interface MailSend {
  id: string;
  email: string;
  subject: string;
  sentAt: string;
  flowId?: string;
}

/** Only the fields a template is made of - the server turns anything else away */
const templateFields = (template: MailTemplate) => ({
  blocks: (template.blocks || []).map((block) => ({
    type: block.type,
    text: block.text || "",
    url: block.url || "",
    imageUrl: block.imageUrl || "",
    alt: block.alt || "",
    align: block.align || "left",
    size: block.size || "",
    // The grid: left out for a block that was never placed, so the server gives it a row of its own
    ...(block.row !== undefined && block.x !== undefined && block.w !== undefined ? { row: block.row, x: block.x, w: block.w } : {}),
  })),
  name: template.name,
  subject: template.subject,
  preheader: template.preheader || "",
  headline: template.headline || "",
  body: template.body,
  buttonText: template.buttonText || "",
  buttonUrl: template.buttonUrl || "",
  imageUrl: template.imageUrl || "",
});

export const getTemplates = async (): Promise<MailTemplate[]> => {
  try {
    return (await axios.get(`${url}/templates`, headers())).data?.results || [];
  } catch (error: any) {
    fail(error, "Kunne ikke hente skabelonerne");
    return [];
  }
};

export const saveTemplate = async (template: MailTemplate): Promise<MailTemplate | null> => {
  try {
    const body = templateFields(template);
    const response = template.id ? await axios.put(`${url}/templates/${template.id}`, body, headers()) : await axios.post(`${url}/templates`, body, headers());
    return response.data;
  } catch (error: any) {
    return fail(error, "Kunne ikke gemme skabelonen");
  }
};

export const deleteTemplate = async (id: string): Promise<boolean> => {
  try {
    await axios.delete(`${url}/templates/${id}`, headers());
    return true;
  } catch (error: any) {
    fail(error, "Kunne ikke slette skabelonen");
    return false;
  }
};

/** What the mail looks like with example values */
export const previewTemplate = async (template: MailTemplate): Promise<{ subject: string; html: string } | null> => {
  try {
    return (await axios.post(`${url}/templates/preview`, templateFields(template), headers())).data;
  } catch (error: any) {
    return null;
  }
};

export const uploadTemplateImage = async (file: File): Promise<string | null> => {
  try {
    const form = new FormData();
    // Store billeder skaleres ned i browseren: en mail viser dem aldrig bredere end 560 px
    form.append("picture", await resizeImage(file));
    return (await axios.post(`${url}/templates/image`, form, { headers: { ...headers().headers, "Content-Type": "multipart/form-data" } })).data?.url || null;
  } catch (error: any) {
    return fail(error, "Billedet kunne ikke uploades");
  }
};

export const getFlows = async (): Promise<MailFlow[]> => {
  try {
    return (await axios.get(`${url}/flows`, headers())).data?.results || [];
  } catch (error: any) {
    fail(error, "Kunne ikke hente flowene");
    return [];
  }
};

export const saveFlow = async (flow: MailFlow): Promise<MailFlow | null> => {
  try {
    const body = { name: flow.name, kind: flow.kind || "marketing", trigger: flow.trigger, waitDays: Number(flow.waitDays) || 0, templateId: flow.templateId, active: Boolean(flow.active) };
    const response = flow.id ? await axios.put(`${url}/flows/${flow.id}`, body, headers()) : await axios.post(`${url}/flows`, body, headers());
    return response.data;
  } catch (error: any) {
    return fail(error, "Kunne ikke gemme flowet");
  }
};

export const deleteFlow = async (id: string): Promise<boolean> => {
  try {
    await axios.delete(`${url}/flows/${id}`, headers());
    return true;
  } catch (error: any) {
    fail(error, "Kunne ikke slette flowet");
    return false;
  }
};

/** Send by hand: as a test to one address, or to every customer who hasn't said no */
export const sendNow = async (templateId: string, audience: "all" | "test", testEmail?: string): Promise<{ sent: number } | null> => {
  try {
    return (await axios.post(`${url}/send`, { templateId, audience, ...(testEmail ? { testEmail } : {}) }, headers())).data;
  } catch (error: any) {
    return fail(error, "Mailen kunne ikke sendes");
  }
};

export const getSends = async (): Promise<MailSend[]> => {
  try {
    return (await axios.get(`${url}/sends`, headers())).data?.results || [];
  } catch (error: any) {
    return [];
  }
};

/** How many customers may be written to at all */
export const getConsentSummary = async (): Promise<{ customers: number; consented: number; unsubscribed: number; missing: number } | null> => {
  try {
    return (await axios.get(`${url}/consent`, headers())).data;
  } catch (error: any) {
    return null;
  }
};
